/**
 * Single source of truth for public-page SEO.
 *
 * Consumed by:
 *  - `RouteSEO` (src/components/flowly/SEO/RouteSEO.tsx) — renders per-route <Helmet>
 *  - `scripts/prerender.ts` — the set of routes snapshotted to static HTML at build
 *  - `scripts/generate-sitemap.ts` — the sitemap URL set
 *
 * Adding SEO for a new flow/page is a TWO-line change: one entry in
 * `PUBLIC_ROUTES` here, and the matching `seo.<seoKey>` strings (he + en) in
 * `src/lib/i18n/translations.ts`.
 */

export const SITE = {
  /** Canonical origin — no trailing slash. Always production, even on previews. */
  baseUrl: "https://app.todowith.ai",
  brand: "TodoAI",
  /** Default social-share image (1200×630). */
  ogImage: "https://app.todowith.ai/og-image.png",
  /** SEO language — the prerendered, indexed version of the site is English. */
  locale: "en_US",
  lang: "en",
  /** Canonical brand-entity @ids — anchored on the apex domain (todowith.ai)
   *  so this SaaS and the agency site (todowithai-site) resolve to ONE
   *  Organization in Google's Knowledge Graph. Keep in sync with index.html
   *  and todowithai-site/src/app/structured-data.tsx. */
  orgId: "https://todowith.ai/#organization",
  founderId: "https://todowith.ai/#person-avihai",
} as const;

/** Keys into the `seo` i18n namespace. Must stay in sync with `translations.<lang>.seo`. */
export type SeoKey =
  | "home"
  | "productCatalog"
  | "productCreative"
  | "imageGenerator"
  | "profilePhoto"
  | "talkingAvatar"
  | "baseballTrend"
  | "textToVideo"
  | "framesToVideo"
  | "imageDirector"
  | "pricing"
  | "help"
  | "contact"
  | "terms"
  | "privacy"
  | "accessibility"
  // Comparison pages — title/description for each <Competitor> vs TodoAI.
  // The competitor's product is embedded in the title; AI engines pick this
  // up as the primary intent of the page.
  | "compareMidjourney"
  | "compareHeygen"
  | "compareKling";

/** A public, indexable route. */
export interface PublicRoute {
  /** URL path, e.g. "/pricing". Root is "/". */
  path: string;
  /** Key into the `seo` i18n namespace for title + description. */
  seoKey: SeoKey;
  /** Sitemap priority hint (0.0–1.0). */
  priority: number;
}

/**
 * Every public, indexable route. Gated routes (/checkout, /profile, /admin),
 * dynamic routes (/s/:id) and feature-flagged community routes are intentionally
 * excluded — they are neither prerendered nor listed in the sitemap.
 */
export const PUBLIC_ROUTES: readonly PublicRoute[] = [
  { path: "/", seoKey: "home", priority: 1.0 },
  { path: "/generate-product-catalog", seoKey: "productCatalog", priority: 0.9 },
  { path: "/product-creative", seoKey: "productCreative", priority: 0.9 },
  { path: "/generate-image", seoKey: "imageGenerator", priority: 0.9 },
  { path: "/profile-photo", seoKey: "profilePhoto", priority: 0.9 },
  { path: "/talking-avatar", seoKey: "talkingAvatar", priority: 0.9 },
  { path: "/baseball-trend", seoKey: "baseballTrend", priority: 0.9 },
  { path: "/text-to-video", seoKey: "textToVideo", priority: 0.9 },
  { path: "/frames-to-video", seoKey: "framesToVideo", priority: 0.9 },
  { path: "/image-director-chat", seoKey: "imageDirector", priority: 0.8 },
  { path: "/pricing", seoKey: "pricing", priority: 0.8 },
  { path: "/help", seoKey: "help", priority: 0.5 },
  { path: "/contact", seoKey: "contact", priority: 0.4 },
  { path: "/terms", seoKey: "terms", priority: 0.2 },
  { path: "/privacy", seoKey: "privacy", priority: 0.2 },
  { path: "/accessibility", seoKey: "accessibility", priority: 0.2 },
  // Comparison pages — high SEO priority because "X vs Y" queries are the
  // single most cited content format by ChatGPT / Perplexity (~33% of all
  // citations per the Princeton GEO study).
  { path: "/compare/midjourney-vs-todo-ai", seoKey: "compareMidjourney", priority: 0.7 },
  { path: "/compare/heygen-vs-todo-ai-talking-avatar", seoKey: "compareHeygen", priority: 0.7 },
  { path: "/compare/kling-vs-todo-ai-text-to-video", seoKey: "compareKling", priority: 0.7 },
] as const;

/** Absolute canonical URL for a route path. */
export function canonicalUrl(path: string): string {
  return path === "/" ? `${SITE.baseUrl}/` : `${SITE.baseUrl}${path}`;
}
